import React, { useState, useEffect } from "react";
import { Rnd } from "react-rnd";

/** First letter of each word, uppercased, max 4 chars. Mirrors SignScreen. */
function computeInitials(name: string | undefined | null): string {
  if (!name) return "AA";
  const initials = name
    .split(/\s+/)
    .filter(Boolean)
    .map((w) => w.charAt(0).toUpperCase())
    .slice(0, 4)
    .join("");
  return initials || "AA";
}

export interface FieldData {
  id: number;
  type: string;
  name: string;
  width: number;
  height: number;
  top: number;
  left: number;
  page: number;
  content?: string;
  /**
   * For inline-mode signature blocks: the id of the signer this field
   * belongs to. The backend paints the signer's signature, name and date
   * at this field's coordinates once they've signed.
   */
  signer_id?: string;
}

interface DraggableResizableFieldProps {
  field: FieldData;
  onDrag: (x: number, y: number) => void;
  onResize: (width: number, height: number) => void;
  onDelete: () => void;
  onTextEdit?: (text: string) => void;
  editorMode?: string;
  fillMode?: boolean;
  /**
   * Visual zoom multiplier applied by the parent. The field is rendered at
   * `field.* * zoom`, but the values handed back to the parent (drag/resize
   * callbacks) are normalized back to the canonical (unzoomed) coord space
   * so stored data and backend math stay zoom-independent.
   */
  zoom?: number;
}

function DraggableResizableField({
  field,
  onDrag,
  onResize,
  onDelete,
  onTextEdit,
  editorMode,
  fillMode = false,
  zoom = 1,
}: DraggableResizableFieldProps) {
  const [text, setText] = useState(field.content || "");
  const [isEditing, setIsEditing] = useState(false);

  // Sync text with field.content when it changes
  useEffect(() => {
    setText(field.content || "");
  }, [field.content]);

  const handleTextChange = (newText: string) => {
    setText(newText);
    if (onTextEdit) {
      onTextEdit(newText);
    }
  };

  const isSignatureOrStamp = ["Signature", "Tampon"].includes(field.type);
  const isImageField = text && text.startsWith("data:image");
  const isTextField = ["Text", "Date"].includes(field.type);
  // Inline "Bon pour accord" block — renders its own 2-row preview so the
  // conductor sees exactly what will land on the signed PDF.
  const isBlock =
    field.type === "Signature" && field.signer_id !== undefined;
  // Standalone initials field — placed independently of the signature block.
  const isInitialsField = field.type === "Initials";

  return (
    <>
      <Rnd
        className="z-10"
        position={{ x: field.left * zoom, y: field.top * zoom }}
        size={{ width: field.width * zoom, height: field.height * zoom }}
        bounds="parent"
        disableDragging={fillMode}
        enableResizing={fillMode ? false : {
          top: false,
          right: true,
          bottom: true,
          left: false,
          bottomRight: true,
          bottomLeft: false,
          topRight: false,
          topLeft: false,
        }}
        resizeHandleStyles={
          fillMode
            ? undefined
            : {
                // Give the bottom-right grip a clickable target big enough
                // to spot and grab. The edges stay narrow but usable.
                bottomRight: {
                  right: "-10px",
                  bottom: "-10px",
                  width: "26px",
                  height: "26px",
                  cursor: "se-resize",
                  zIndex: 30,
                },
                right: { width: "8px", cursor: "ew-resize" },
                bottom: { height: "8px", cursor: "ns-resize" },
              }
        }
        resizeHandleComponent={
          fillMode
            ? undefined
            : {
                bottomRight: (
                  <div
                    className="bg-black rounded-full flex items-center justify-center hover:bg-neutral-700 shadow"
                    style={{ width: 24, height: 24 }}
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      viewBox="0 0 24 24"
                      fill="white"
                      width="14"
                      height="14"
                    >
                      <path d="M22 22H20V20H22V22M22 18H20V16H22V18M18 22H16V20H18V22M18 18H16V16H18V18M14 22H12V20H14V22M22 14H20V12H22V14Z" />
                    </svg>
                  </div>
                ),
              }
        }
        onResizeStop={(e, direction, ref) => {
          // Rnd reports sizes in the rendered (zoomed) space. Divide by
          // zoom to keep stored field dimensions canonical.
          const newWidth = parseInt(ref.style.width, 10) / zoom;
          const newHeight = parseInt(ref.style.height, 10) / zoom;
          onResize(newWidth, newHeight);
        }}
        onDragStop={(e, d) => {
          // Same reason: Rnd's `d.x/d.y` are in the rendered (zoomed) space.
          onDrag(d.x / zoom, d.y / zoom);
        }}
      >
        <div className="w-full h-full relative">
          {isBlock ? (
            <div
              className="w-full h-full flex flex-col items-stretch justify-between border-2 border-dashed border-neutral-700 rounded-lg overflow-hidden bg-white"
              style={{ cursor: fillMode ? "default" : "move" }}
            >
              <div
                className="flex-1 flex items-center justify-center bg-blue-100 text-neutral-800 font-semibold px-2 text-center"
                style={{ fontSize: `${Math.max(Math.min(field.width, field.height) * 0.14 * zoom, 10)}px` }}
              >
                Bon pour accord
              </div>
              <div
                className="flex-1 flex items-center justify-center bg-white text-neutral-500 px-2 text-center"
                style={{ fontSize: `${Math.max(Math.min(field.width, field.height) * 0.12 * zoom, 9)}px` }}
              >
                JJ/MM/AAAA
              </div>
              <div
                className="flex-[2] flex items-center justify-center bg-neutral-50 text-neutral-400 italic px-2 text-center border-t border-dashed border-neutral-300"
                style={{ fontSize: `${Math.max(Math.min(field.width, field.height) * 0.12 * zoom, 9)}px` }}
              >
                {field.name || "Signataire"}
              </div>
            </div>
          ) : isInitialsField ? (
            <div
              className="w-full h-full flex flex-col items-stretch justify-between border-2 border-dashed border-amber-500 rounded-lg overflow-hidden bg-amber-50"
              style={{ cursor: fillMode ? "default" : "move" }}
            >
              <div
                className="flex-1 flex items-center justify-center text-amber-700 font-semibold tracking-wider px-2 text-center"
                style={{ fontSize: `${Math.max(Math.min(field.width, field.height) * 0.42 * zoom, 11)}px` }}
              >
                {computeInitials(field.name)}
              </div>
              <div
                className="text-[9px] flex items-center justify-center bg-white/70 text-amber-700 px-2 text-center border-t border-dashed border-amber-300"
                style={{ fontSize: `${Math.max(Math.min(field.width, field.height) * 0.16 * zoom, 8)}px` }}
              >
                Initiales · {field.name || "Signataire"}
              </div>
            </div>
          ) : (
            <div
              className="w-full h-full items-center justify-center border-2 border-black border-dashed relative flex bg-blue-300 opacity-65 rounded-lg overflow-hidden"
              style={{
                fontSize: !isSignatureOrStamp
                  ? `${Math.min(field.width, field.height) * 0.4 * zoom}px`
                  : `${14 * zoom}px`,
                cursor: fillMode ? "pointer" : "move",
              }}
              onClick={fillMode && !isTextField ? () => {} : undefined}
            >
              {isImageField ? (
                <img src={text} alt={field.name} className="w-full h-full object-contain" />
              ) : fillMode && isTextField && isEditing ? (
                <input
                  type="text"
                  value={text}
                  onChange={(e) => handleTextChange(e.target.value)}
                  onBlur={() => setIsEditing(false)}
                  autoFocus
                  className="w-full h-full px-2 bg-transparent border-none outline-none text-center"
                  style={{
                    fontSize: `${Math.min(field.width, field.height) * 0.4 * zoom}px`,
                  }}
                />
              ) : fillMode && isTextField ? (
                <span
                  className="truncate px-2 cursor-text"
                  onClick={() => setIsEditing(true)}
                >
                  {text || "Cliquez pour saisir"}
                </span>
              ) : (
                <span className="truncate px-2">{text || field.name || field.type}</span>
              )}
            </div>
          )}

          {!fillMode && (
            <div
              onClick={onDelete}
              className="absolute bg-black rounded-full cursor-pointer p-1 z-20 hover:bg-red-500 -top-3 -right-3"
              style={{ width: 24, height: 24 }}
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 24 24"
                fill="white"
                width="16"
                height="16"
              >
                <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z" />
              </svg>
            </div>
          )}
        </div>
      </Rnd>
    </>
  );
}

export default DraggableResizableField;
